import {Injectable} from '@angular/core';
import {DaoGeneric} from '@Services/base/daoService';
import {FormModel, FormType, ReponseState, ResponseStateInstruction} from '../../components/form/models';
import {HttpClient, HttpEvent} from '@angular/common/http';
import {environment} from '@Env/environment';
import {ResponseForm} from '../../components/form/models/responsedemat.model';
import {combineLatest, Observable, Subject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {ResponseStateGestModel} from '../../components/form/response-states/response-state-gest-model';
import {ResponseInstruction} from '../instructions/response-instruction.model';
import {FilterFieldCustomEnabled} from './filter-field-custom-enabled.model';
import {FilterFieldResponseEnabled} from './filter-field-response-enabled.model';
import {DomSanitizer} from '@angular/platform-browser';
import {SafeHtmlPipe} from '@Common/widgets/pipes';
import {ModaleResponseComponent} from '../../../gestion/reponses/modale-response/modale-response.component';
import {HelperService} from '../helper.service';
import {MatDialog} from '@angular/material/dialog';
import {UtilsService} from '@Services/core/utils/utils.service';
import * as FileSaver from 'file-saver';
import * as dateFnsAdaptor from 'date-fns';
import {FilesUtils} from '@Services/common';

@Injectable({providedIn: 'root'})
export class ResponsesService extends DaoGeneric<ResponseForm> {
  /**
   * RAZ filtres des champs paramétrés ResponseValueDemat
   */
  public filterCustomRazSubject = new Subject<FilterFieldCustomEnabled>();
  onFilterCustomRaz$ = this.filterCustomRazSubject.asObservable();
  public filterCustomRazAllSubject = new Subject();
  onFilterCustomRazAll$ = this.filterCustomRazAllSubject.asObservable();
  /**
   * RAZ filtres des champs ResponseDemat
   */
  public filterResponseRazSubject = new Subject<FilterFieldResponseEnabled>();
  onFilterResponseRaz$ = this.filterResponseRazSubject.asObservable();
  public filterResponseRazAllSubject = new Subject();
  onFilterResponseRazAll$ = this.filterResponseRazAllSubject.asObservable();
  private urlResponsesForm = `${environment.baseUrl}/demat/response/`;
  private apiResponseValue = `${environment.baseUrl}/demat/response_value/`;
  private responseBlockAdministratifSubject = new Subject<boolean>();
  onSendBlockAdministratif$ = this.responseBlockAdministratifSubject.asObservable();

  constructor(private httpClient: HttpClient,
              private helperSvc: HelperService,
              private fileUtilsSvc: FilesUtils,
              private dialog: MatDialog,
              private sanitized: DomSanitizer) {
    super(httpClient);
  }

  getRootUrl(): string {
    return this.urlResponsesForm;
  }

  /**
   * Upload fichiers
   * @param filesToUpload
   * @param responseId
   * @param editMode
   */
  uploadFiles(filesToUpload, responseId, editMode = false) {
    const url = `${this.apiResponseValue}save_files_values/`;
    const requests = [];
    const fieldName = editMode ? 'id' : 'field_id';
    for (const key in filesToUpload) {
      if (filesToUpload[key] === undefined || key === undefined || key === null) {
        console.error(`Invalid file or key: ${key}`, filesToUpload[key]);
        continue;
      }
      const formData = new FormData();
      formData.append(fieldName, key.toString());
      formData.append('file', filesToUpload[key]);
      formData.append('response_id', responseId.toString());
      formData.append('value', filesToUpload[key].name);
      requests.push(this.httpClient.post(url, formData));
    }
    return combineLatest([...requests]);
  }

  /**
   * Upload d'un fichier sur une valeur de réponse existante, avec événements de progression
   * @param file fichier à envoyer
   * @param responseValueId pk de la ResponseValueDemat
   * @param responseId pk de la demande
   */
  uploadFileWithProgress(file: File, responseValueId: number, responseId: number): Observable<HttpEvent<any>> {
    const formData = new FormData();
    formData.append('id', responseValueId.toString());
    formData.append('file', file);
    formData.append('response_id', responseId.toString());
    formData.append('value', file.name);
    return this.httpClient.post(`${this.apiResponseValue}save_files_values/`, formData, {
      reportProgress: true,
      observe: 'events'
    });
  }

  getResponseForOpinion(uuid: string) {
    const url = `${this.urlResponsesForm}${uuid}/get_response_for_opinion/`
    return this.httpClient.get<ResponseForm>(url);
  }

  getResponseExport(uuid: string, type: 'pdf' | 'zip'): Observable<any> {
    const baseUrl = `${this.urlResponsesForm}${uuid}/`;
    const path = type === 'pdf' ? 'get_pdf_responses/' : 'get_zip_responses/';
    const url = `${baseUrl}${path}`;
    return this.httpClient.get<Blob>(url, {responseType: 'blob' as 'json'});
  }

  downloadDemande(response: ResponseForm, type: 'pdf' | 'zip' = 'pdf') {
    const demandeur = this.fileUtilsSvc.slugify(`${response.user_obj.last_name} ${response.user_obj.first_name}`);
    const name = `${demandeur}-${response.reference}-exporte-le-${this.formatDateForNamePdfOrZip()}.${type}`;
    return this.getResponseExport(response.uuid, type).pipe(
      tap(blob => FileSaver.saveAs(blob, name))
    );
  }

  formatDateForNamePdfOrZip() {
    const formattedDate = dateFnsAdaptor.format(new Date(), 'dd-MM-yyyy-hhmm');
    return (formattedDate.slice(0, 13) + 'h' + formattedDate.slice(13));
  }

  getFileResponse(value): Observable<any> {
    const url = `${this.apiResponseValue}${value.id}/get_file/`;
    return this.httpClient.get(url, {responseType: 'blob'}).pipe(
      map((data: any) => new Blob([data]))
    );
  }

  deleteFileResponse(value): Observable<any> {
    const url = `${this.apiResponseValue}${value.id}/delete_file/`;
    return this.httpClient.delete(url);
  }

  getResponseById(id: number) {
    return this.httpClient.get<ResponseForm>(`${this.getRootUrl()}response_by_id/${id}/`);
  }

  /**
   * Export excel des réponses filtrées
   * @param formUUID
   * @param extraParams
   * @param extraDict
   */
  exportExcel(formUUID: string,
              extraParams: Map<string, string> = null, extraDict: Map<string, string[]> = null) {
    const url = `${this.urlResponsesForm}export_excel/`;
    if (! extraParams) {
      extraParams = new Map<string, string>();
    }
    extraParams.set('form_uuid', formUUID);
    const _extraParams = this._getPaginationParams(0, 0, null, extraParams);
    const _extraParamsAndDict = this._getUrlHttpDict(extraDict, _extraParams);
    return this.httpClient.get(url, {params: _extraParamsAndDict, responseType: 'blob'});
  }
  /**
   * Les réponses à un formulaire, archivées ou non
   * @param {string} uuid : uuid du formulaire concerné
   * @param {boolean} archivedOnly : les réponses non archivées ou archivées
   */
  getUrlReponses(uuid: string, archivedOnly = false) {
    let url = `${this.urlResponsesForm}responses_by_form/?form_uuid=${uuid}`;
    url = `${url}&archived=${archivedOnly}`;
    return url;
  }

  /**
   * Get by UUID
   * TODO : à enlever AMHA
   * @param uuid
   */
  getByUUID(uuid: string): Observable<ResponseForm> {
    const params = {
      'uuid': uuid
    };
    const url = `${this.getRootUrl()}get_by_uuid/`;
    return this.httpClient
      .get<ResponseForm>(url, {params})
      .pipe(catchError((error: any) => this._throwObservable(error)));
  }

  /**
   * Get by UUID for sollicite
   * @param uuid
   */
  getByUUIDForSollicite(uuid: string): Observable<ResponseForm> {
    const url = `${this.getRootUrl()}${uuid}/get_response_for_sollicite/`;
    return this.httpClient
      .get<ResponseForm>(url)
      .pipe(catchError((error: any) => this._throwObservable(error)));
  }

  /**
   * Envoi du bloc administratif pour cette réponse
   * @param response
   */
  sendValuesBlockAdmin(response) {
    const url = `${this.apiResponseValue}update_blk_admin_values/`;
    return this.httpClient
      .post(url, response)
      .pipe(tap((_status: boolean) => this.responseBlockAdministratifSubject.next(_status)));
  }

  /**
   * Etat demande / réponse du demandeur
   * @param state
   */
  stateLabel(state: ReponseState) {
    const stateMap: Map<ReponseState, string> = new Map<ReponseState, string>([
      [ReponseState.draft, 'Brouillon'],
      [ReponseState.published, 'Envoyée'],
      [ReponseState.archived, 'Archivée']
    ]);
    return stateMap.get(state) || '';
  }
  /**
   * Renvoie le "Chip" / span couleur selon l'état de la demande
   */
  stateDisplay(state: ReponseState) {
    const sp: SafeHtmlPipe = new SafeHtmlPipe(this.sanitized);
    const stateLabel = this.stateLabel(state);
    let display = '';
    switch (state) {
      case ReponseState.draft:
        display = `<span style="border-radius: 10px; background-color: lightgrey; padding: 5px; color: black;">${stateLabel.toLowerCase()}</span>`;
        break;
      case ReponseState.published:
        display = `<span style="border-radius: 10px; background-color: green; padding: 5px; color: white;">${stateLabel.toLowerCase()}</span>`;
        break;
      case ReponseState.archived:
        display = `<span style="border-radius: 10px; background-color: orange; padding: 5px; color: white;">${stateLabel.toLowerCase()}</span>`;
        break;
    }
    return sp.transform(display);
  }
  /**
   * Obtenir l'instruction la plus récente / dernière
   * @param {ResponseInstruction[]} instructions
   */
  stateLastInstruction(instructions: ResponseInstruction[]): ResponseInstruction {
    instructions.sort((_stateA: ResponseInstruction, _stateB: ResponseInstruction) =>
      (_stateA.dt_created > _stateB.dt_created) ? -1 : ((_stateB.dt_created > _stateA.dt_created) ? 1 : 0));
    return instructions[0];
  }

  /**
   * Etat instruction d'un gestionnaire sur une demande / réponse
   * @param {ResponseStateInstruction} state
   * @param {FormModel} form
   */
  public stateInstructionLabel(state: ResponseStateInstruction, form: FormModel = null) {
    const _labelAccepted = form?.is_accepted_like_admissible ? 'Recevable' : 'Acceptée';
    const stateMap: Map<ResponseStateInstruction, string> = new Map<ResponseStateInstruction, string>([
      [ResponseStateInstruction.in_progress, 'En traitement'],
      [ResponseStateInstruction.accepted, _labelAccepted],
      [ResponseStateInstruction.refused, 'Refusée'],
      [ResponseStateInstruction.closed, 'Classée sans suite'],
      [ResponseStateInstruction.incomplete, 'Incomplète']
    ]);
    return stateMap.get(state) || '';
  }

  /**
   * Est-ce que l'état de l'instruction est un statut obligatoire pour le motif ?
   * @param state
   */
  isMotifStateRequired(state: ResponseStateInstruction) {
    switch (state) {
      case ResponseStateInstruction.refused:
      case ResponseStateInstruction.incomplete:
      case ResponseStateInstruction.closed:
        return true;
      case ResponseStateInstruction.accepted:
      default:
        return false;
    }
  }
  optionalOrRequiredLabel(state: ResponseStateInstruction, minCarctereMotif: number) {
    if (this.isMotifStateRequired(state)) {
      return ['obligatoire', `obligatoire, ${minCarctereMotif} caractères minimum`];
    }
    return ['facultatif', 'facultatif'];
  }
  private _spanInstructionStyle(stateModel: ResponseStateGestModel) {
    return `<span style="border-radius: 10px; background-color: ${stateModel?.color}; color: ${stateModel?.colorFont || 'black'}; padding: 5px;">
                ${stateModel.label.toLowerCase()}</span>`;
  }

  /**
   * Renvoie le span "chip" avec le style selon l'instruction
   * un sondage n'ayant pas d'instruction, il est directement traité par le type de formulaire concerné
   * @param {ResponseForm} response
   * @return par ex. <span style="border-radius: 10px; background-color: green; color: white; padding: 5px;">acceptée</span>
   */
  stateInstructionDisplayColor(response: ResponseForm) {
    const sp: SafeHtmlPipe = new SafeHtmlPipe(this.sanitized);
    let display = this._spanInstructionStyle(<ResponseStateGestModel> {color: 'lightgrey', colorFont: 'black', label: 'non instruit'});
    if (response.form_obj.type === FormType.Sondage) {
      const stateModel = this.stateResponseInstructionModel(response.last_instruction_obj?.state,
        response.form_obj?.type,
        response.form_obj);
      return sp.transform(this._spanInstructionStyle(stateModel));
    }
    if (response.last_instruction_obj) {
      const stateModel = this.stateResponseInstructionModel(response.last_instruction_obj?.state,
        response.form_obj?.type,
        response.form_obj);
      display = this._spanInstructionStyle(stateModel);
    }
    return sp.transform(display);
  }

  /**
   * Selon le paramétrage du formulaire is_accepted_like_admissible, le libellé "Acceptée" est transposé en "Recevable".
   * @param formModel
   * @param state
   * @private
   */
  private _getLabelAccepted(formModel: FormModel, state: ResponseStateInstruction) {
    let _accepted : ResponseStateGestModel = {
      order: 0, icon: 'check_circle', color: 'green', colorFont: 'white', state: ResponseStateInstruction.accepted,
      label: this.stateInstructionLabel(state), explain: 'Le demandeur sera notifié que sa demande a été acceptée'
    }
    if (formModel?.is_accepted_like_admissible) {
      _accepted.label = 'Recevable';
      _accepted.explain = 'Le demandeur sera notifié que sa demande est recevable';
    }
    return _accepted;
  }
  public stateResponseInstructionModel(state: ResponseStateInstruction,
                                       formType: FormType = FormType.Demande,
                                       formModel: FormModel = null): ResponseStateGestModel {
    if (formType === FormType.Sondage) {
      return <ResponseStateGestModel> {
        label: 'sondage', color: 'yellow', colorFont: 'black'
      };
    } else {
      const _accepted = this._getLabelAccepted(formModel, state);
      const stateMap: Map<ResponseStateInstruction, ResponseStateGestModel> = new Map<ResponseStateInstruction, ResponseStateGestModel>([
        [ResponseStateInstruction.in_progress,
          <ResponseStateGestModel>{
            order: -2, icon: 'build', color: 'blue', colorFont: 'white',
            state: ResponseStateInstruction.in_progress,
            label: this.stateInstructionLabel(state),
            explain: 'Passer la demande en cours de traitement, le demandeur ne pourra plus modifier sa demande'
          }],
        [ResponseStateInstruction.accepted, _accepted],
        [ResponseStateInstruction.refused,
          <ResponseStateGestModel>{
            order: 1, icon: 'cancel', color: 'red', colorFont: 'white', state: ResponseStateInstruction.refused,
            label: this.stateInstructionLabel(state), explain: 'Le demandeur sera notifié que sa demande a été refusée'
          }],
        [ResponseStateInstruction.closed,
          <ResponseStateGestModel>{
            order: 2,
            icon: 'more_horiz',
            color: 'orange', colorFont: 'white',
            state: ResponseStateInstruction.closed,
            label: this.stateInstructionLabel(state),
            explain: 'Le demandeur sera notifié que sa demande a été classée sans suite'
          }],
        [ResponseStateInstruction.incomplete,
          <ResponseStateGestModel>{
            order: -3,
            icon: 'reply',
            color: 'orange', colorFont: 'white',
            state: ResponseStateInstruction.incomplete,
            label: this.stateInstructionLabel(state),
            explain: 'Le demandeur sera notifié que sa demande est incomplète et pourra la modifier'
          }],
      ]);
      return stateMap.get(state);
    }
  }
  stateStateGestModelWaiting(capitalizeFirstLetter = true): ResponseStateGestModel {
    return {
      label: capitalizeFirstLetter ? UtilsService.ucFirst(this.stateLabelWaiting()) : this.stateLabelWaiting(),
      state: -1,
      icon: 'hourglass_empty',
      order: -10,
      color: 'grey', colorFont: 'black',
      explain: 'Le demandeur est en attente de traitement de sa demande'
    };
  }

  /**
   * Statut lorsqu'il n'y a pas encore d'instruction
   */
  stateLabelWaiting() {
    return 'en attente';
  }

  /**
   * Obtention des états d'instruction d'une demande, "françisés"
   * @param {ResponseStateInstruction[]} listStates : liste des états à générer (tous si null/undefined)
   * @param {FormModel} formModel
   */
  statesResponseInstruction(listStates: ResponseStateInstruction[] = null,
                            formModel: FormModel= null): ResponseStateGestModel[] {
    const states: ResponseStateGestModel[] = [];
    Object.entries(ResponseStateInstruction).forEach(_state => {
      if (Number.isInteger(_state[1])) {
        const state = Number(_state[1]);
        if (listStates) {
          if (listStates.find(_st => _st === state) !== undefined) {
            states.push(this.stateResponseInstructionModel(state, FormType.Demande, formModel));
          }
        } else {
          states.push(this.stateResponseInstructionModel(state, FormType.Demande, formModel));
        }
      }
    });
    states.sort((_stateA, _stateB) => (_stateA.order > _stateB.order) ? 1 : ((_stateB.order > _stateA.order) ? -1 : 0));
    return states;
  }

  /**
   * Ouvre une réponse avec ses valeurs dans une modale, elle s'adapte à la taille de l'écran
   * Il faut que response contienne response_objs (valeurs)
   * @param {ResponseForm} response
   * @param modal
   */
  openInModaleResponse(response: ResponseForm, modal = ModaleResponseComponent) {
    const [screenHeight, screenWidth] = this.helperSvc.getScreenSize();
    const projectModale = this.dialog.open(modal, {data: response});
    const top = 50;
    const width = screenWidth - 50;
    const height = screenHeight - (2 * top);
    projectModale.updatePosition({top: `${top}px`});
    projectModale.updateSize(`${width}px`, `${height}px`);
  }
  isAlreadyManagedState(state: ResponseStateInstruction) {
    return [ResponseStateInstruction.accepted,
      ResponseStateInstruction.refused,
      ResponseStateInstruction.closed].includes(state);
  }
  isAlreadyManaged(response: ResponseForm) {
    return this.isAlreadyManagedState(response?.last_instruction_obj?.state);
  }

  updateResponseValueById(data): Observable<any>{
    const url = `${this.apiResponseValue}update_response_by_id/`;
    return this.httpClient.post<any>(url, data);
  }
}
